import { useEffect, useState } from "react";
import MiramarCalculator from "@/components/MiramarCalculator";
import axios from "axios";
import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowRight, Clock, CalendarBlank, Phone, CaretRight, CheckCircle } from "@phosphor-icons/react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { getPost, POSTS, BLOG_BASE } from "@/blogData";
import { SITE, PHONE_DISPLAY, PHONE_TEL } from "@/seoData";
import usePricing from "@/hooks/usePricing";
import { applyPrices } from "@/priceTokens";
import { sizedImg } from "@/lib/utils";

const fmtDate = (d) =>
  new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

function Block({ b }) {
  if (b.type === "h2") return <h2 className="font-display font-extrabold text-2xl lg:text-3xl text-[#15306E] mt-10 mb-3 tracking-tight">{b.text}</h2>;
  if (b.type === "p") return <p className="text-[#3F3F46] leading-relaxed text-[17px] mb-4">{b.text}</p>;
  if (b.type === "ul")
    return (
      <ul className="space-y-2.5 mb-5">
        {b.items.map((it) => (
          <li key={it} className="flex items-start gap-2.5 text-[#3F3F46] text-[16px] leading-relaxed">
            <CheckCircle size={18} weight="fill" className="text-[#46A01E] mt-1 shrink-0" /> {it}
          </li>
        ))}
      </ul>
    );
  if (b.type === "cta")
    return (
      <div className="my-8 bg-[#F4FAF0] border border-[#DCEAD0] rounded-sm p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
        <p className="font-display font-extrabold text-[#15306E] text-lg">{b.text}</p>
        <a href={`tel:${PHONE_TEL}`} className="shrink-0 inline-flex items-center gap-2 bg-[#46A01E] hover:bg-[#3A8418] text-white font-display font-extrabold uppercase tracking-wide text-sm px-5 py-3.5 rounded-sm transition-colors">
          <Phone size={18} weight="fill" /> {PHONE_DISPLAY}
        </a>
      </div>
    );
  return null;
}

const DEFAULT_RELATED = [
  { to: "/pricing-and-disposal-guide", label: "Pricing & Disposal Guide" },
  { to: "/junk-removal/san-diego", label: "Junk Removal in San Diego" },
];

// Old guide slugs merged into a canonical post — redirect to preserve link equity.
const BLOG_REDIRECTS = {
  "where-to-dispose-of-a-mattress-san-diego": "how-to-dispose-of-a-mattress-san-diego",
  "junk-removal-cost-san-diego": "/junk-removal-cost",
};

export default function BlogPost() {
  const { slug } = useParams();
  const redirectTo = BLOG_REDIRECTS[slug];
  const staticPost = getPost(slug);
  const [post, setPost] = useState(staticPost || null);
  const pricing = usePricing();
  const [loading, setLoading] = useState(!staticPost);
  const [others, setOthers] = useState(POSTS.filter((p) => p.slug !== slug).slice(0, 2));

  useEffect(() => {
    window.scrollTo(0, 0);
    setPost(getPost(slug) || null);
    setLoading(!getPost(slug));
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/guides/${slug}`)
      .then(({ data }) => { if (data) setPost(data); })
      .catch(() => {})
      .finally(() => setLoading(false));
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/guides`)
      .then(({ data }) => Array.isArray(data) && setOthers(data.filter((p) => p.slug !== slug).slice(0, 2)))
      .catch(() => {});
  }, [slug]);

  if (redirectTo) return <Navigate to={redirectTo.startsWith("/") ? redirectTo : `/blog/${redirectTo}`} replace />;
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#15306E]">Loading…</div>;
  if (!post) return <Navigate to="/blog" replace />;

  const pp = applyPrices(post, pricing);
  const canonical = `${BLOG_BASE}/${pp.slug}`;
  const related = pp.related && pp.related.length ? pp.related : DEFAULT_RELATED;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: pp.title,
      description: pp.description,
      image: pp.hero,
      datePublished: pp.date,
      dateModified: pp.date,
      author: { "@type": "Organization", name: "CleanOut247" },
      publisher: { "@type": "Organization", name: "CleanOut247", logo: { "@type": "ImageObject", url: SITE + "/og-logo.png" } },
      mainEntityOfPage: canonical,
      url: canonical,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: pp.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    },
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": canonical + "#webpage",
      url: canonical,
      speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "[data-speakable]"] },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE + "/" },
        { "@type": "ListItem", position: 2, name: "Guides", item: `${SITE}/blog` },
        { "@type": "ListItem", position: 3, name: pp.title, item: canonical },
      ],
    },
  ];

  return (
    <div className="bg-white text-[#0A0A0A] overflow-x-hidden" data-testid="blog-post-page">
      <Seo title={pp.seoTitle} description={pp.description} canonical={canonical} jsonLd={jsonLd} />
      <Header />

      <article className="pt-24 sm:pt-36 lg:pt-44 pb-8 px-5 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <nav className="flex items-center gap-1.5 text-xs text-[#71717A] mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#46A01E]">Home</Link>
            <CaretRight size={12} />
            <Link to="/blog" className="hover:text-[#46A01E]">Guides</Link>
            <CaretRight size={12} />
            <span className="text-[#15306E] font-semibold truncate">{pp.title}</span>
          </nav>

          <span className="inline-block bg-[#EAF4E3] text-[#15306E] text-xs font-semibold px-2.5 py-1 rounded-sm mb-4">{pp.category}</span>
          <h1 className="font-display font-black tracking-tighter text-3xl sm:text-4xl lg:text-5xl text-[#15306E] leading-tight">{pp.title}</h1>
          {pp.answer && (
            <p data-speakable data-testid="blog-post-answer" className="mt-5 border-l-4 border-[#46A01E] bg-[#F4FAF0] px-5 py-4 text-lg text-[#15306E] leading-relaxed">
              <strong>Quick answer:</strong> {pp.answer}
            </p>
          )}
          <div className="flex items-center gap-4 text-sm text-[#71717A] mt-4">
            <span className="flex items-center gap-1.5"><CalendarBlank size={15} weight="fill" /> {fmtDate(pp.date)}</span>
            <span className="flex items-center gap-1.5"><Clock size={15} weight="fill" /> {pp.readTime}</span>
          </div>
        </div>

        <div className="max-w-4xl mx-auto mt-8 rounded-sm overflow-hidden">
          <img src={sizedImg(pp.hero, 1200)} alt={pp.title} fetchpriority="high" className="w-full h-64 sm:h-80 lg:h-[420px] object-cover" />
        </div>

        <div className="max-w-3xl mx-auto mt-10" data-testid="blog-post-body">
          {pp.body.map((b, i) => (
            <Block key={i} b={b} />
          ))}
        </div>

        {pp.slug === "miramar-landfill-guide" && <MiramarCalculator />}

        {/* FAQ */}
        <div className="max-w-3xl mx-auto mt-14">
          <h2 className="font-display font-extrabold text-2xl lg:text-3xl text-[#15306E] mb-6 tracking-tight">Frequently asked questions</h2>
          <div className="space-y-4">
            {pp.faqs.map((f) => (
              <div key={f.q} className="border border-[#E4E4E7] rounded-sm p-5">
                <p className="font-display font-bold text-[#15306E]">{f.q}</p>
                <p className="text-[#52525B] mt-2 leading-relaxed text-[15px]" data-speakable>{f.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Related links */}
        <div className="max-w-3xl mx-auto mt-14 pt-8 border-t border-[#E4E4E7]">
          <h3 className="font-display font-extrabold text-lg text-[#15306E] mb-4">Related</h3>
          <div className="flex flex-wrap gap-2.5">
            {related.map((r) => (
              <Link key={r.to} to={r.to} data-testid={`blog-related-${r.to.replace(/[^a-z0-9]/gi, "-")}`} className="inline-flex items-center gap-1.5 border border-[#E4E4E7] hover:border-[#46A01E] text-[#15306E] text-sm font-semibold px-4 py-2.5 rounded-sm transition-colors">
                {r.label} <ArrowRight size={14} weight="bold" />
              </Link>
            ))}
          </div>
        </div>
      </article>

      {/* More guides */}
      <section className="px-5 lg:px-8 py-16 bg-[#F4F4F5]">
        <div className="max-w-3xl mx-auto">
          <h3 className="font-display font-extrabold text-xl text-[#15306E] mb-6">More guides</h3>
          <div className="grid sm:grid-cols-2 gap-5">
            {applyPrices(others, pricing).map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="group bg-white border border-[#E4E4E7] rounded-sm overflow-hidden hover:border-[#46A01E] transition-all">
                <div className="aspect-[16/9] overflow-hidden">
                  <img src={sizedImg(p.hero, 640)} alt={p.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-5">
                  <p className="font-display font-extrabold text-[#15306E] leading-snug group-hover:text-[#46A01E] transition-colors">{p.title}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
